// The "Video Crash Courses" and "Math Vault" promo boxes are authored into the LaTeX/Overleaf
// source and arrive as part of the article HTML stored in Sanity (`encyclopediaStructure.htmlContent`).
// They cannot be turned off in the CMS, so any surface that must not advertise them has to remove
// the markup from the HTML string before it is rendered.
//
// Only these two classes are promo boxes. Sibling `tcolorbox` variants seen in the content
// (`fooinfo`, `fooqaexample`, `fooqathink`, `foorule`, `foothink`, ...) are pedagogical and must survive.
const PROMO_BOX_CLASS_NAMES = ['foobootcamp', 'foomathvault']

// Opening `<div ...>` tags and closing `</div>` tags, in document order.
const DIV_TAG_PATTERN = /<div\b[^>]*>|<\/div\s*>/gi

// `class` attribute with a double-quoted, single-quoted or unquoted value.
const CLASS_ATTRIBUTE_PATTERN = /\bclass\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s"'>]+))/i

interface PromoBoxRange {
  start: number
  end: number
}

interface ScanState {
  /** Number of `<div>` elements open before the current tag. */
  depth: number
  /** Index of the opening tag of the outermost promo box currently open, or null when outside one. */
  openBoxStart: number | null
  /** Depth the current promo box was opened at, so we can find its matching `</div>`. */
  openBoxDepth: number
  ranges: PromoBoxRange[]
}

const INITIAL_SCAN_STATE: ScanState = { depth: 0, openBoxStart: null, openBoxDepth: 0, ranges: [] }

const isPromoBoxOpeningTag = (tag: string): boolean => {
  const classAttribute = CLASS_ATTRIBUTE_PATTERN.exec(tag)
  if (!classAttribute) {
    return false
  }

  const classNames = (classAttribute[1] ?? classAttribute[2] ?? classAttribute[3] ?? '').split(/\s+/)
  return PROMO_BOX_CLASS_NAMES.some((promoClassName) => classNames.includes(promoClassName))
}

const scanTag = (state: ScanState, tag: RegExpExecArray): ScanState => {
  if (!tag[0].startsWith('</')) {
    const opensPromoBox = state.openBoxStart === null && isPromoBoxOpeningTag(tag[0])
    return {
      depth: state.depth + 1,
      openBoxStart: opensPromoBox ? tag.index : state.openBoxStart,
      openBoxDepth: opensPromoBox ? state.depth : state.openBoxDepth,
      ranges: state.ranges,
    }
  }

  const depth = state.depth - 1

  if (state.openBoxStart !== null && depth === state.openBoxDepth) {
    return {
      depth,
      openBoxStart: null,
      openBoxDepth: state.openBoxDepth,
      ranges: [...state.ranges, { start: state.openBoxStart, end: tag.index + tag[0].length }],
    }
  }

  return { ...state, depth }
}

/** Joins the gaps between (and around) the given ordered, non-overlapping ranges. */
const cutRanges = (html: string, ranges: PromoBoxRange[]): string =>
  ranges.length === 0
    ? html
    : ranges
        .map((range, index) => html.slice(index === 0 ? 0 : ranges[index - 1].end, range.start))
        .concat(html.slice(ranges[ranges.length - 1].end))
        .join('')

/**
 * Removes the "Video Crash Courses" (`foobootcamp`) and "Math Vault" (`foomathvault`) promo boxes
 * from an article HTML string, including their nested children.
 *
 * The boxes contain nested `<div>`s, so the closing tag is found by depth counting rather than by
 * a non-greedy regex (which would cut at the first `</div>` and mangle the rest of the article).
 * Anything else in the HTML — including every other `tcolorbox` variant — is left byte-identical.
 * An unbalanced promo box (no matching `</div>`) is left in place rather than truncating the document.
 */
export const stripPromoBoxes = (html?: string | null): string => {
  if (!html) {
    return ''
  }

  if (!PROMO_BOX_CLASS_NAMES.some((promoClassName) => html.includes(promoClassName))) {
    return html
  }

  const { ranges } = Array.from(html.matchAll(DIV_TAG_PATTERN)).reduce(scanTag, INITIAL_SCAN_STATE)

  return cutRanges(html, ranges)
}
